import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Button, ButtonGroup, ButtonGroupItem, Dropdown, Form, Input } from '@boozallen-uip/uswds-react-core';
import { useNavigate } from 'react-router-dom';
import last from 'lodash/last';
import { FormField, FormFieldBody, FormFieldContainer, FormSectionContainer, FormSectionHeader, FormSectionHeaderContainer, FormSectionSubHeader, PageSubHeader, SectionContainer } from '../shared.styled';
import { UserContext } from '../UserContext';
import { BASE_TITLE, ENTITY_TYPE_AGENCY, VENDOR_OVERVIEW_URL, stateLabelValues } from '../../utlities/core.constants';
import { getApiErrorMessage, validateZipCode } from '../../utlities/common.funcs';
import useEntity from '../../hooks/useEntity.hook';
import usePrimaryPoc from '../../hooks/usePrimaryPoc.hook';


function FormFieldRequired() {
  return <span className='text-red'>(Required)</span>
}

// Logged-in axios resolves statuses below 500, so a 4xx body is a normal return value.
// Throw it so the submit handler stays on the form instead of navigating as a success.
function throwIfFailedResponse(response) {
  const status = response?.status;
  if (typeof status === 'number' && (status < 200 || status >= 300)) {
    const err = new Error("Save failed");
    err.response = response;
    throw err;
  }
}

export default function VendorEdit() {
  const [ vendor, setVendor ] = useState(null);
  const [ vendorPatch, setVendorPatch] = useState({});
  const [ primaryPoc, setPrimaryPoc ] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [zipError, setZipError] = useState(null);
  const [saveError, setSaveError] = useState('');
  const errorRef = useRef(null);
  const { userEntity, refreshMyEntity } = useContext(UserContext);
  const { updateVendor, updateAgency } = useEntity();
  const { getPrimaryPoc, updatePrimaryPoc, submitPrimaryPoc } = usePrimaryPoc();

  const navigate = useNavigate();

  const isAgency = userEntity?.type === ENTITY_TYPE_AGENCY;

  document.title = `${BASE_TITLE} - ${isAgency ? 'Agency' : 'Vendor'} Edit`;

  // Save org details to the correct endpoint based on the org type. This also lets an impersonating
  // SuperUser edit a Buyer (Agency) org, whose details previously had no matching edit path.
  const saveOrg = useCallback((patch) => {
    if (isAgency) {
      return updateAgency(userEntity.entity.id, patch);
    }
    return updateVendor({ ...patch, id: userEntity.entity.id });
  }, [isAgency, updateAgency, updateVendor, userEntity]);

  useEffect(() => {
    if(!userEntity){
      return;
    }

    (async() => {
      setVendor({...userEntity.entity});
      if(userEntity.entity.primaryPocId){
        const primaryPocResponse = await getPrimaryPoc(userEntity.entity.primaryPocId);
        setPrimaryPoc(primaryPocResponse);
      }
    })();

  }, [userEntity])

  useEffect(() => {
    if (saveError && errorRef.current) {
      errorRef.current.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
  }, [saveError]);

  const updatePatch = (patch) => {
    setVendor({...vendor, ...patch})
    setVendorPatch({...vendorPatch, ...patch})
  };
  const onSubmitHandler = useCallback(async () => {
    setSaveError('');

    const zipValidationMessage = validateZipCode(primaryPoc.zipCode);
    if (zipValidationMessage) {
      setZipError(zipValidationMessage);
      return;
    }
    setZipError(null);

    try {
      setIsSaving(true);
      const primaryPocToSave = { ...primaryPoc, zipCode: String(primaryPoc.zipCode ?? '').trim() };

      // Update org information (vendor or agency)
      await saveOrg({...vendorPatch});

      // Update or create primary POC
      if(primaryPocToSave.id){
        const pocResponse = await updatePrimaryPoc(primaryPocToSave.id, {...primaryPocToSave });
        throwIfFailedResponse(pocResponse);
      } else {
        const primaryResponse = await submitPrimaryPoc({...primaryPocToSave });
        throwIfFailedResponse(primaryResponse);
        if (!primaryResponse?.data?.id) {
          const err = new Error("Unable to save primary contact");
          err.response = primaryResponse;
          throw err;
        }
        await saveOrg({...vendorPatch, primaryPocId: primaryResponse.data.id});
      }

      // Refresh entity data
      await refreshMyEntity();

      // Navigate with success state - if we got here, all operations succeeded
      navigate(VENDOR_OVERVIEW_URL, {
        state: {
          success: true,
          message: "Your page edits were saved successfully."
        }
      });
    } catch(e) {
      // eslint-disable-next-line no-console
      console.error(e);
      // Stay on the form so a rejected save (e.g. an invalid ZIP the API caught) is visible and
      // recoverable, instead of silently bouncing back to the overview as if nothing happened.
      setSaveError(getApiErrorMessage(e, "We couldn't save your changes. Please review the form and try again."));
    } finally {
      setIsSaving(false);
    }
  },[vendor, primaryPoc, vendorPatch, userEntity, saveOrg, updatePrimaryPoc, submitPrimaryPoc, refreshMyEntity, navigate]);

  if (!vendor) {
    return <section />;
  }

  return (
    <div>
      <SectionContainer className='pt-4'>
        <div className='text-primary-darker'>
          <a className="text-primary-darker" href="/">Home &gt; Repository &gt; </a> For Vendors
        </div>
        <PageSubHeader>Manage {vendor.name}{vendor.name && last(vendor.name) === "s" ? `s'` : `'s`} Overview Page</PageSubHeader>
        <p className='mb-8rem'>
          Please fill out the form below to create a new listing for your product. The information you provide will appear on the product overview page, as well as the product listing table. It is important to include as much detail as possible to help buyers make an informed decision about your product.
        </p>
        {saveError && (
          <div ref={errorRef} className="mb-4" role="alert" aria-live="assertive">
            <Alert type="error" heading="Save failed" onClose={() => setSaveError('')}>
              {saveError}
            </Alert>
          </div>
        )}
        <Form id="productForm" onSubmit={onSubmitHandler} className='w-full max-w-full'>
          <FormSectionContainer>
              <FormSectionHeaderContainer>
                <FormSectionHeader>Company information</FormSectionHeader>
                <FormSectionSubHeader className='mb-0'>
                  <p>Please provide your company&apos;s information as you would like it to appear on its Overview Page.</p>
                  <p className='mb-0'>
                    <span className='font-bold'>Note: </span>Including a “doing business as” (DBA) name will override the company name in the page’s title.
                  </p>
                </FormSectionSubHeader>
              </FormSectionHeaderContainer>
              <FormFieldContainer>
                <FormFieldBody>
                  <FormField>
                    <Input id="vendor-company-name" value={vendor.name} onChange={(e) => updatePatch({ name: e.target.value })} required>
                      Company name <FormFieldRequired />
                    </Input>
                  </FormField>
                  <FormField>
                    <Input id="vendor-dba" value={vendor.dba}  onChange={(e) => updatePatch({  dba: e.target.value })}>
                      DBA, if applicable
                    </Input>
                  </FormField>
                  <FormField className='col-start-1'>
                    <Input id="vendor-description" value={vendor.description}  type="textarea" onChange={(e) => updatePatch({ description: e.target.value })}>
                      Company description  <FormFieldRequired />
                    </Input>
                  </FormField>
                </FormFieldBody>
              </FormFieldContainer>
            </FormSectionContainer>
            <FormSectionContainer className='col-span-8'>
            <FormSectionHeaderContainer>
              <FormSectionHeader>Company contact information</FormSectionHeader>
                <FormSectionSubHeader>Provide general contact information for your company’s accessibility department or personnel.</FormSectionSubHeader>
              </FormSectionHeaderContainer>
              <FormFieldContainer>
                <FormFieldBody>
                <FormField className='col-start-1'>
                      <Input id="vendor-primary-poc-phone" value={primaryPoc.phoneNumber}  onChange={(e) => setPrimaryPoc({ ...primaryPoc, phoneNumber: e.target.value })}>
                        Phone number <FormFieldRequired />
                      </Input>
                    </FormField>
                    <FormField>
                      <Input id="vendor-primary-poc-email" value={primaryPoc.email}  onChange={(e) => setPrimaryPoc({ ...primaryPoc, email: e.target.value })}>
                        Email address <FormFieldRequired />
                      </Input>
                    </FormField>

                    <FormField className='col-start-1'>
                      <Input id="vendor-primary-poc-street1" value={primaryPoc.street1}  onChange={(e) => setPrimaryPoc({ ...primaryPoc, street1: e.target.value })}>
                        Street Address Line 1
                      </Input>
                    </FormField>
                    <FormField>
                      <Input id="vendor-primary-poc-street2" value={primaryPoc.street2}  onChange={(e) => setPrimaryPoc({ ...primaryPoc, street2: e.target.value })}>
                        Street Address Line 2
                      </Input>
                    </FormField>

                    <FormField className='col-start-1'>
                      <Input id="vendor-primary-poc-city" value={primaryPoc.city}  onChange={(e) => setPrimaryPoc({ ...primaryPoc, city: e.target.value })}>
                        City
                      </Input>
                    </FormField>
                    <FormField>
                      <Dropdown id="state"
                        optionRenderer={(o) => o.label}
                        options={stateLabelValues}
                        value={primaryPoc.state}
                        comparator={(value,option) => value.value === option}
                        onChange={e => setPrimaryPoc({ ...primaryPoc, state: e.detail.value.value })}>
                        State
                      </Dropdown>
                    </FormField>
                    <FormField className='col-start-1'>
                      <Input id="vendor-primary-poc-zip" value={primaryPoc.zipCode} onChange={(e) => { setZipError(null); setPrimaryPoc({ ...primaryPoc, zipCode: e.target.value }); }}>
                        ZIP Code
                      </Input>
                      {zipError && <div className="mt-1 text-sm text-red" role="alert">{zipError}</div>}
                    </FormField>
                </FormFieldBody>
              </FormFieldContainer>
            </FormSectionContainer>
            <section>
              <ButtonGroup>
                <ButtonGroupItem>
                  <Button type="button" outline onClick={() => navigate(VENDOR_OVERVIEW_URL)}>Cancel</Button>
                </ButtonGroupItem>
                <ButtonGroupItem>
                  <Button type="button" disabled={isSaving} onClick={() => { onSubmitHandler(); }}>
                    Submit
                  </Button>
                </ButtonGroupItem>
              </ButtonGroup>
            </section>
        </Form>
      </SectionContainer>
    </div>)
}
